import { useQuery } from '@tanstack/react-query'
import { InfoNotice } from '@/components/patterns/info-notice'
import { LoadingState } from '@/components/patterns/loading-state'
import { Button } from '@/components/ui/button'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import type { CreatedCart, PosCart } from '@/lib/services/carts/carts.types'
import { locationQueries } from '@/lib/services/locations/locations.query'
import { POSCart } from './POSCart'

/** A location as the POS pickup and delivery dialogs read it: its address, hours and delivery fees. */
export const POS_LOCATION_FILTERS = {
  include: ['address', 'availabilityChanges', 'deliveryPostalCodeFees', 'deliveryAreaFees'],
}

/** The location query the order change's cart reads, for the route loader to prefetch. */
export function orderChangeLocationQuery(organizationId: number, locationId: number) {
  return locationQueries.show({ organizationId, locationId, filters: POS_LOCATION_FILTERS })
}

interface OrderChangeCartPanelProps {
  cart: PosCart
  createCartWithDefaults: () => Promise<CreatedCart>
  /** Told when the change's Pay Now opens or closes. */
  onPayNowOpenChange: (open: boolean) => void
  className?: string
}

/**
 * The cart of an order change. A change edits the order's fulfillment with the cart's own pickup and delivery
 * dialogs, which validate against the full location; the cart carries only a summary of it, so the order's location
 * is loaded here, and the cart opens only once it has: without it, pickup hours and delivery areas would go unchecked.
 */
export function OrderChangeCartPanel({
  cart,
  createCartWithDefaults,
  onPayNowOpenChange,
  className,
}: OrderChangeCartPanelProps) {
  const organizationId = useRouteOrganizationId()
  const locationId = cart.location_id ?? null
  const {
    data: location = null,
    isError,
    isFetching,
    refetch,
  } = useQuery({
    ...orderChangeLocationQuery(organizationId, locationId ?? 0),
    enabled: locationId !== null,
  })

  if (locationId !== null && location === null) {
    return isError ? (
      <InfoNotice
        tone="warning"
        title="We couldn’t load this order’s shop"
        body="Pickup and delivery need the shop’s hours and delivery areas."
        action={
          <Button variant="notice" size="xs" onClick={() => void refetch()} disabled={isFetching}>
            Try again
          </Button>
        }
        className={className}
      />
    ) : (
      <LoadingState label="Loading this order’s shop…" className={className} />
    )
  }

  return (
    <POSCart
      cart={cart}
      location={location}
      defaultLocation={location}
      createCartWithDefaults={createCartWithDefaults}
      onOrderChangePayNowOpenChange={onPayNowOpenChange}
      className={className}
    />
  )
}
